import { useEffect, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { Drawer, Box, IconButton, CircularProgress } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DrawerNavigation from "./drawer/DrawerNavigation";
import DrawerContent from "./drawer/DrawerContent";
import ThumbnailPreview from "./drawer/ThumbnailPreview";
import { FileIcon } from "./shared/FileIcon";
import { useDrawerNavigation } from "../hooks/useDrawerNavigation";
import { useThumbnailOperations } from "../hooks/useThumbnailOperations";
import {
  isPdfFile,
  isVideoFile,
  isPreviewableAsPdf,
  isPreviewableAsHtml,
} from "../hooks/useFileOperations";
import {
  getDropboxFolder,
  clearSelectedFolder,
  clearError,
} from "../../../../redux/slices/dropboxFoldersSlice";

const DropboxFolderDrawer = ({
  open,
  folderId,
  userRole,
  onClose,
  navigateToPath,
  highlightPath,
  onClearHighlight,
  hideEmptyFolders,
}: any) => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    selectedFolder,
    folderLoading,
    error,
    thumbnails,
    singleThumbnails,
    thumbnailsLoading,
    temporaryLinks,
    temporaryLinkLoading,
    previews,
    previewLoading,
  } = useAppSelector((state) => state.dropboxFolders);

  const {
    navigationStack,
    currentFolder,
    setCurrentFolder,
    handleFolderClick,
    handleBackClick,
    handleBreadcrumbClick,
    resetNavigation,
    navigateToFolder,
    collectFilePathsAndFetchThumbnails,
  } = useDrawerNavigation(selectedFolder);

  const {
    thumbnailPreviewOpen,
    selectedThumbnailFile,
    handleThumbnailClick,
    closeThumbnailPreview,
    resetThumbnailState,
  } = useThumbnailOperations();

  useEffect(() => {
    if (open && folderId) {
      dispatch(getDropboxFolder(folderId));
      resetNavigation();
      resetThumbnailState();
    }
  }, [open, folderId, dispatch, resetNavigation, resetThumbnailState]);

  useEffect(() => {
    if (selectedFolder) {
      setCurrentFolder(selectedFolder);
      collectFilePathsAndFetchThumbnails(selectedFolder);
    }
  }, [selectedFolder, setCurrentFolder, collectFilePathsAndFetchThumbnails]);

  // Auto-navigate to the folder containing the target file when navigateToPath is set
  useEffect(() => {
    if (!selectedFolder || !navigateToPath) return;

    // Find the path to the parent folder of the target file in the children tree
    const targetPath = navigateToPath.toLowerCase();
    // Get the parent directory of the target file
    const lastSlash = targetPath.lastIndexOf("/");
    const parentDirPath =
      lastSlash > 0 ? targetPath.substring(0, lastSlash) : null;
    if (!parentDirPath) return;

    // Recursively search the tree for the folder whose path matches parentDirPath
    const findPathToFolder = (node: any, target: any, trail: any): any[] | null => {
      if (!node || !node.children) return null;

      for (const child of node.children) {
        const childPath = (
          child.path ||
          child.path_display ||
          ""
        ).toLowerCase();
        if (child.type === "folder" && childPath === target) {
          return [...trail, child];
        }
        if (child.type === "folder" && child.children) {
          const result: any[] | null = findPathToFolder(child, target, [...trail, child]);
          if (result) return result;
        }
      }
      return null;
    };

    const pathTrail = findPathToFolder(selectedFolder, parentDirPath, []);
    if (pathTrail && pathTrail.length > 0) {
      navigateToFolder(pathTrail);
    }
  }, [selectedFolder, navigateToPath, navigateToFolder]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Error loading folder: ${error}`, { variant: "error" });
      dispatch(clearError());
    }
  }, [error, enqueueSnackbar, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(clearSelectedFolder());
    resetNavigation();
    resetThumbnailState();
    onClose();
  }, [dispatch, resetNavigation, resetThumbnailState, onClose]);

  const handleFolderClickWrapper = useCallback(
    (folder: any) => {
      if (folder.type === "folder" && folder.children) {
        // Clear thumbnail preview and highlight when navigating to a new folder
        resetThumbnailState();
        if (onClearHighlight) onClearHighlight();
        handleFolderClick(folder);
      } else if (
        folder.type === "file" &&
        folder.path &&
        thumbnails[folder.path]
      ) {
        // If it's a file with a thumbnail, open the thumbnail preview
        handleThumbnailClick(folder);
      }
    },
    [resetThumbnailState, handleFolderClick, thumbnails, handleThumbnailClick],
  );

  const handleBackClickWrapper = useCallback(() => {
    // Clear thumbnail preview when navigating back
    resetThumbnailState();
    handleBackClick();
  }, [resetThumbnailState, handleBackClick]);

  const handleBreadcrumbClickWrapper = useCallback(
    (index: number) => {
      // Clear thumbnail preview when navigating via breadcrumbs
      resetThumbnailState();
      handleBreadcrumbClick(index);
    },
    [resetThumbnailState, handleBreadcrumbClick],
  );

  const renderFileIconOrThumbnail = useCallback(
    (file: any) => {
      if (file.type !== "file") {
        return <FileIcon fileName={file.name} type={file.type} />;
      }

      const thumbnail = thumbnails[file.path];

      if (thumbnailsLoading && !thumbnail) {
        return (
          <Box
            sx={{
              width: 64,
              height: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 0,
              bgcolor: "grey.100",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        );
      }

      if (thumbnail) {
        return (
          <img
            src={`data:image/jpeg;base64,${thumbnail}`}
            alt={file.name}
            onClick={(e) => {
              e.stopPropagation();
              handleThumbnailClick(file);
            }}
            style={{
              width: 64,
              height: 64,
              objectFit: "cover",
              borderRadius: 0,
              cursor: "pointer",
            }}
          />
        );
      }

      return (
        <Box
          sx={{
            width: 64,
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FileIcon fileName={file.name} type={file.type} />
        </Box>
      );
    },
    [thumbnails, thumbnailsLoading, handleThumbnailClick],
  );

  const renderImageThumbnail = useCallback(
    (file: any) => {
      const thumbnail = thumbnails[file.path];

      if (thumbnailsLoading && !thumbnail) {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "grey.100",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        );
      }

      if (thumbnail) {
        return (
          <img
            src={`data:image/jpeg;base64,${thumbnail}`}
            alt={file.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        );
      }

      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
          }}
        >
          <FileIcon fileName={file.name} type={file.type} />
        </Box>
      );
    },
    [thumbnails, thumbnailsLoading],
  );

  const handleThumbnailNavigate = useCallback(
    (imageFile: any) => {
      handleThumbnailClick(imageFile);
    },
    [handleThumbnailClick],
  );

  return (
    <>
      {/* Main Drawer - List */}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(19, 21, 23, 0.45)",
          },
          "& .MuiDrawer-paper": {
            width: {
              xs: "100%",
              sm: 480,
              md: 480,
              lg: 480,
            },
            maxWidth: "100%",
            mt: 7,
            borderRadius: 0,
            height: "calc(100% - 56px)",
            backgroundColor: "var(--surface-container-lowest, #ffffff)",
            border: "1px solid var(--outline-variant, #dcdcdc)",
            transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
          },
        }}
      >
        <Box
          sx={{
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <DrawerNavigation
            selectedFolder={selectedFolder}
            navigationStack={navigationStack}
            currentFolder={currentFolder}
            onBackClick={handleBackClickWrapper}
            onBreadcrumbClick={handleBreadcrumbClickWrapper}
            userRole={userRole}
          />

          {/* Close Button */}
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2,
            }}
          >
            <IconButton onClick={handleClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 2, overflow: "auto" }}>
            <DrawerContent
              currentFolder={currentFolder}
              folderLoading={folderLoading}
              error={error}
              onFolderClick={handleFolderClickWrapper}
              renderFileIconOrThumbnail={renderFileIconOrThumbnail}
              renderImageThumbnail={renderImageThumbnail}
              selectedThumbnailFile={selectedThumbnailFile}
              highlightPath={highlightPath}
              hideEmptyFolders={hideEmptyFolders}
            />
          </Box>
        </Box>
      </Drawer>

      {/* Thumbnail Preview Drawer */}
      <Drawer
        anchor="right"
        open={thumbnailPreviewOpen}
        hideBackdrop
        onClose={closeThumbnailPreview}
        sx={{
          width: "calc(100% - 480px)",
          "& .MuiDrawer-paper": {
            backgroundColor: "var(--surface-container-lowest, #ffffff)",
            border: "1px solid var(--outline-variant, #dcdcdc)",
            transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
            width: {
              xs: "100%",
              sm:
                isPdfFile(selectedThumbnailFile?.name) ||
                isVideoFile(selectedThumbnailFile?.name) ||
                isPreviewableAsPdf(selectedThumbnailFile?.name) ||
                isPreviewableAsHtml(selectedThumbnailFile?.name)
                  ? "calc(100vw - 480px)"
                  : 400,
              md:
                isPdfFile(selectedThumbnailFile?.name) ||
                isVideoFile(selectedThumbnailFile?.name) ||
                isPreviewableAsPdf(selectedThumbnailFile?.name) ||
                isPreviewableAsHtml(selectedThumbnailFile?.name)
                  ? "calc(100vw - 480px)"
                  : 500,
              lg:
                isPdfFile(selectedThumbnailFile?.name) ||
                isVideoFile(selectedThumbnailFile?.name) ||
                isPreviewableAsPdf(selectedThumbnailFile?.name) ||
                isPreviewableAsHtml(selectedThumbnailFile?.name)
                  ? "calc(100vw - 480px)"
                  : 600,
            },
            maxWidth: "100%",
            mt: { xs: "calc(56px + 10px)", sm: "calc(64px + 10px)" },
            borderRadius: 0,
            height: { xs: "calc(100% - 76px)", sm: "calc(100% - 84px)" },
            right: {
              xs: 0,
              sm: 480,
              md: 480,
              lg: 480,
            },
          },
        }}
      >
        <ThumbnailPreview
          selectedFile={selectedThumbnailFile}
          thumbnailPreviewOpen={thumbnailPreviewOpen}
          onClose={closeThumbnailPreview}
          thumbnails={thumbnails}
          singleThumbnails={singleThumbnails}
          temporaryLinks={temporaryLinks}
          previews={previews}
          temporaryLinkLoading={temporaryLinkLoading}
          previewLoading={previewLoading}
          thumbnailsLoading={thumbnailsLoading}
          currentFolder={currentFolder}
          onNavigateToImage={handleThumbnailNavigate}
        />
      </Drawer>
    </>
  );
};

export default DropboxFolderDrawer;
